import React, { useContext, useEffect } from 'react';
import { Breadcrumbs } from '@prisjakt/ui/breadcrumbs';
import { useAppContext } from 'src/components/app-context';
import HeaderBlock from 'src/areas/product-collection/components/header-block';
import { PageContent } from 'src/components/page';
import {
  InternalNavigationScope,
  trackInternalNavigationClick,
} from 'src/globals/tracking/tracking';
import {
  useKWLPDeprioritizeNoImageOffers,
  useKWLPLimitProducts,
} from 'src/hooks/experiments';
import type { KeywordLandingPageQuery } from 'src/types';
import type { Discriminate } from 'utils/types';
import { IsKWLPContext } from '../contexts';
import {
  trackKWLPNoImages,
  trackKWLPNoItems,
  trackKWLPOffersOnlyVisited,
} from '../tracking';
import { CategoryResults } from './categories';
import { NoItems } from './noItems';
import { ProductResults } from './products';

import { useMatchedRoute } from 'src/hooks/use-matched-route';
import { useKeywordLandingPageQuery } from '../queries/useKeywordLandingPage';
import Loader from 'src/components/loader';
import { Link } from 'react-router-dom';
import { BreadCrumbWrapper, SearchResults } from './styles';
import { WideContext } from 'src/contexts/wide-context';

export const KeywordLandingPageLoader = () => {
  const route = useMatchedRoute();
  const limit = useKWLPLimitProducts();
  const { setIsWide } = useContext(WideContext);

  const { slug, query } = route?.match?.params ?? {};

  const keywordLandingPageQuery = useKeywordLandingPageQuery({
    slug,
    query: query ? decodeURI(query) : undefined,
    offset: 0,
    limit,
  });

  const { data, isLoading, isSuccess } = keywordLandingPageQuery;

  useEffect(() => {
    setIsWide(true);

    return () => {
      setIsWide(false);
    };
  }, [setIsWide]);

  if (!isLoading && !isSuccess) {
    return null;
  }

  return (
    <IsKWLPContext.Provider value>
      <PageContent withAds topPlacements={[]} data-test="KeywordLandingPage">
        {isLoading ? <Loader /> : null}
        {data ? <KeywordLandingPage data={data} /> : null}
      </PageContent>
    </IsKWLPContext.Provider>
  );
};

export default KeywordLandingPageLoader;

export type Product = Discriminate<
  KeywordLandingPageQuery['keywordLandingPage']['items']['nodes'][number],
  '__typename',
  'Product'
>;

export type Offer = Discriminate<
  KeywordLandingPageQuery['keywordLandingPage']['items']['nodes'][number],
  '__typename',
  'Offer'
>;

export type Item = Product | Offer;

interface Props {
  data: KeywordLandingPageQuery;
}

const KeywordLandingPage = ({
  data: { keywordLandingPage: data, popularSearches },
}: Props) => {
  const { t } = useAppContext();

  const {
    url,
    items: { nodes: items },
    categories: { nodes: categories },
    title,
  } = data;

  const [products, offers] = items.reduce<[Product[], Offer[]]>(
    ([ps, os], po) => {
      if (po.__typename === 'Product') {
        ps.push(po);
      } else {
        os.push(po);
      }
      return [ps, os];
    },
    [[], []]
  );

  const isOffersOnly = products.length === 0 && offers.length >= 1;

  if (isOffersOnly && title && url) {
    trackKWLPOffersOnlyVisited(title, url);
  }

  const itemsWithoutImageIDs = items
    .filter(item => !item.media.first)
    .map(item => (item.__typename === 'Product' ? item.id : item.offerId));
  if (itemsWithoutImageIDs.length) {
    trackKWLPNoImages(data.url, itemsWithoutImageIDs.join(','));
  }

  const breadcrumbPath = [
    ...(data.parentCategory?.path ?? []),
    {
      id: title,
      name: title,
      pathName: '',
    },
  ];

  // Only include products or offers from featured stores
  // Also add this type so that we use the fancy alias instead
  let finalItems: Item[] = items.filter(
    i => i.__typename !== 'Offer' || i.store.featured
  );

  const deprioritizeNoImageOffers = useKWLPDeprioritizeNoImageOffers();
  if (deprioritizeNoImageOffers) {
    finalItems = finalItems
      .reduce<[Item[], Item[]]>(
        ([withImage, withoutImage], curr) => {
          if (curr?.__typename === 'Offer' && !curr.media.first) {
            withoutImage.push(curr);
          } else {
            withImage.push(curr);
          }
          return [withImage, withoutImage];
        },
        [[], []]
      )
      .flat();
  }

  if (!(data.published && finalItems.length)) {
    trackKWLPNoItems(title, url);
  }

  return (
    <>
      <BreadCrumbWrapper>
        <Breadcrumbs
          containerProps={{
            onClick: (nextPage: string) => {
              trackInternalNavigationClick({
                scope: InternalNavigationScope.BREADCRUMB,
                nextPage,
              });
            },
          }}
        >
          <Link to="/">{t('common', 'breadcrumb.home')}</Link>
          {breadcrumbPath.map((item, index) => {
            return breadcrumbPath.length - 1 === index ? (
              <span key={item.id} data-test="Crumb">
                {item.name}
              </span>
            ) : (
              <Link data-test="Crumb" key={item.id} to={item.pathName}>
                {item.name}
              </Link>
            );
          })}
        </Breadcrumbs>
      </BreadCrumbWrapper>
      <HeaderBlock noUppercase title={title} />
      {data.published && finalItems.length ? (
        <SearchResults>
          <CategoryResults categories={categories} />
          <ProductResults title={title} items={finalItems} />
        </SearchResults>
      ) : (
        <NoItems popularSearches={popularSearches} />
      )}
    </>
  );
};
